/**
 * MEDIA-HEIC-SERVER-PREVIEW-PROFILE-A1 — native/server prepare state machine.
 *
 * No browser HEIC decoder dependency. Capability truth = actual decode attempt.
 * A successful ImageBitmap is not itself a valid File source for AvatarCropEditor;
 * native readiness requires a non-empty JPEG File materialization.
 */

import { isHeicLike } from "../imageFileContract";
import {
  CONFIRM_SERVER_SURFACES,
  NATIVE_MAX_PIXELS,
  PREPARE_STATES,
  SERVER_ROUTE_BYTE_THRESHOLD,
} from "./constants";
import {
  requestServerImagePreview,
  ServerPreviewError,
} from "./requestServerPreview";

let globalRunSeq = 0;

export function nextPrepareRunId() {
  globalRunSeq += 1;
  return globalRunSeq;
}

export function mapPrepareErrorMessage(code, fallback) {
  const messages = {
    missing_file: "Потрібен файл зображення.",
    invalid_surface: "Невідома поверхня попереднього перегляду.",
    file_too_large: "Файл перевищує максимальний розмір 10 МБ.",
    metadata_mismatch: "Формат файлу не відповідає заявленому типу зображення.",
    decoded_non_heif: "Підтримуються лише HEIC/HEIF для попереднього перегляду.",
    corrupt_or_unsupported_heif: "Файл не є коректним HEIC/HEIF зображенням.",
    pixel_policy_rejected:
      "Зображення перевищує дозволені розміри для попереднього перегляду.",
    decoder_unavailable: "Підтримка HEIC/HEIF тимчасово недоступна на сервері.",
    throttled: "Забагато запитів. Спробуйте пізніше.",
    processing_failed: "Тимчасова помилка обробки. Спробуйте пізніше.",
    not_authenticated: "Необхідна автентифікація.",
    permission_denied: "Доступ заборонено.",
    cancelled_ui: "Підготовку скасовано.",
    native_failed: "Не вдалося прочитати це зображення локально.",
    confirmation_declined: "Підготовку скасовано.",
  };
  return messages[code] || fallback || messages.processing_failed;
}

async function defaultNativeDecode(file, deps) {
  const createImageBitmapFn =
    deps.createImageBitmap ||
    (typeof createImageBitmap === "function" ? createImageBitmap : null);
  if (!createImageBitmapFn) {
    throw new Error("createImageBitmap unavailable");
  }
  const bitmap = await createImageBitmapFn(file);
  return {
    width: bitmap.width,
    height: bitmap.height,
    bitmap,
    cleanup: () => {
      if (bitmap && typeof bitmap.close === "function") bitmap.close();
    },
  };
}

function closeDecoded(decoded) {
  if (!decoded) return;
  try {
    if (typeof decoded.cleanup === "function") {
      decoded.cleanup();
      return;
    }
  } catch (_err) {
    // ignore
  }
  try {
    if (decoded.bitmap && typeof decoded.bitmap.close === "function") {
      decoded.bitmap.close();
    }
  } catch (_err) {
    // ignore
  }
}

/**
 * Materialize ImageBitmap → non-empty JPEG File for AvatarCropEditor.
 * Returns null when canvas/context/toBlob/File cannot produce a usable File.
 */
export async function materializeBitmapToJpegFile(decoded, options = {}) {
  const width = decoded.width || 0;
  const height = decoded.height || 0;
  const bitmap = decoded.bitmap;
  if (!bitmap || width <= 0 || height <= 0) {
    return null;
  }

  let canvas = null;
  try {
    canvas =
      options.createCanvas?.(width, height) ||
      (typeof document !== "undefined"
        ? Object.assign(document.createElement("canvas"), {
            width,
            height,
          })
        : null);
  } catch (_err) {
    return null;
  }
  if (!canvas) {
    return null;
  }

  let ctx = null;
  try {
    ctx = canvas.getContext("2d");
  } catch (_err) {
    return null;
  }
  if (!ctx) {
    return null;
  }

  try {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0);
  } catch (_err) {
    return null;
  }

  let blob = null;
  try {
    blob = await new Promise((resolve, reject) => {
      if (typeof canvas.toBlob !== "function") {
        resolve(null);
        return;
      }
      try {
        canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92);
      } catch (err) {
        reject(err);
      }
    });
  } catch (_err) {
    return null;
  }
  if (!blob || !blob.size) {
    return null;
  }

  const FileCtor = Object.prototype.hasOwnProperty.call(options, "File")
    ? options.File
    : typeof File !== "undefined"
      ? File
      : null;
  if (!FileCtor) {
    return null;
  }
  try {
    const file = new FileCtor([blob], "native-preview.jpg", {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
    if (!file || !file.size) {
      return null;
    }
    return file;
  } catch (_err) {
    return null;
  }
}

/**
 * Prepare a still image File for the square editor.
 *
 * Non-HEIC files return { kind: 'passthrough', file } without HEIC prepare.
 * HEIC-like files follow the frozen native/server machine.
 *
 * @returns {Promise<{
 *   kind: 'passthrough'|'native'|'server'|'cancelled'|'failed',
 *   state: string,
 *   runId: number,
 *   file?: File,
 *   code?: string,
 *   detail?: string,
 *   cleanup?: Function,
 * }>}
 */
export async function prepareImageForEditor(file, options = {}) {
  const runId = options.runId != null ? options.runId : nextPrepareRunId();
  const surface = options.surface || "profile_avatar";
  const onStateChange = options.onStateChange || (() => {});
  const isCurrent = options.isCurrent || (() => true);
  const signal = options.signal;
  const owned = [];

  const stillActive = () => isCurrent() && !(signal && signal.aborted);

  const emit = (state, extra = {}) => {
    if (!stillActive()) return;
    onStateChange({ state, runId, ...extra });
  };

  const cleanupOwned = () => {
    while (owned.length) {
      const fn = owned.pop();
      try {
        fn();
      } catch (_err) {
        // ignore cleanup errors
      }
    }
  };

  const cancelledResult = () => {
    cleanupOwned();
    emit(PREPARE_STATES.cancelled_ui);
    return {
      kind: "cancelled",
      state: PREPARE_STATES.cancelled_ui,
      runId,
    };
  };

  if (!file) {
    emit(PREPARE_STATES.failed, { code: "missing_file" });
    return {
      kind: "failed",
      state: PREPARE_STATES.failed,
      runId,
      code: "missing_file",
      detail: mapPrepareErrorMessage("missing_file"),
    };
  }

  emit(PREPARE_STATES.selected);

  // Non-HEIC still images keep existing local editor path.
  if (!isHeicLike(file)) {
    return {
      kind: "passthrough",
      state: PREPARE_STATES.native_ready,
      runId,
      file,
    };
  }

  const needsConfirm = CONFIRM_SERVER_SURFACES.includes(surface);
  const confirmServerPreview =
    options.confirmServerPreview ||
    (async () => {
      if (typeof window !== "undefined" && typeof window.confirm === "function") {
        return window.confirm(
          options.confirmMessage ||
            "Оригінальне зображення буде безпечно надіслано на сервер для підготовки редагованого попереднього перегляду. Продовжити?",
        );
      }
      return false;
    });

  const routeToServer = async () => {
    if (!stillActive()) {
      return cancelledResult();
    }

    if (needsConfirm) {
      const confirmed = await confirmServerPreview();
      if (!stillActive()) {
        return cancelledResult();
      }
      if (!confirmed) {
        cleanupOwned();
        emit(PREPARE_STATES.cancelled_ui, { code: "confirmation_declined" });
        return {
          kind: "cancelled",
          state: PREPARE_STATES.cancelled_ui,
          runId,
          code: "confirmation_declined",
          detail: mapPrepareErrorMessage("confirmation_declined"),
        };
      }
    }

    if (!stillActive()) {
      return cancelledResult();
    }

    emit(PREPARE_STATES.server_preview_pending);
    const requestPreview =
      options.requestServerPreview || requestServerImagePreview;

    try {
      const previewFile = await requestPreview(file, surface, {
        signal,
        apiClient: options.apiClient,
        File: options.File,
      });
      if (!stillActive()) {
        return cancelledResult();
      }
      emit(PREPARE_STATES.server_preview_ready);
      return {
        kind: "server",
        state: PREPARE_STATES.server_preview_ready,
        runId,
        file: previewFile,
        cleanup: cleanupOwned,
      };
    } catch (err) {
      cleanupOwned();
      if (!stillActive()) {
        return {
          kind: "cancelled",
          state: PREPARE_STATES.cancelled_ui,
          runId,
        };
      }
      if (err?.aborted || err?.code === "cancelled_ui") {
        emit(PREPARE_STATES.cancelled_ui);
        return {
          kind: "cancelled",
          state: PREPARE_STATES.cancelled_ui,
          runId,
        };
      }
      const code =
        err instanceof ServerPreviewError
          ? err.code
          : err?.code || "processing_failed";
      const detail = mapPrepareErrorMessage(
        code,
        err?.detail || err?.message,
      );
      emit(PREPARE_STATES.failed, { code, detail });
      return {
        kind: "failed",
        state: PREPARE_STATES.failed,
        runId,
        code,
        detail,
      };
    }
  };

  // Immediate server route for HEIC-like files > 8 MiB (advisory).
  const size = typeof file.size === "number" ? file.size : 0;
  if (size > SERVER_ROUTE_BYTE_THRESHOLD) {
    return routeToServer();
  }

  emit(PREPARE_STATES.native_check_pending);
  const nativeDecode = options.nativeDecode || defaultNativeDecode;

  try {
    const decoded = await nativeDecode(file, options);
    if (!stillActive()) {
      closeDecoded(decoded);
      return cancelledResult();
    }

    const pixels = (decoded.width || 0) * (decoded.height || 0);
    if (pixels > NATIVE_MAX_PIXELS) {
      closeDecoded(decoded);
      return routeToServer();
    }

    if (typeof decoded.cleanup === "function") {
      owned.push(decoded.cleanup);
    } else if (decoded.bitmap && typeof decoded.bitmap.close === "function") {
      owned.push(() => decoded.bitmap.close());
    }

    // Bitmap alone is not a valid AvatarCropEditor File source. Materialize
    // a non-empty JPEG File; on any materialization failure close the bitmap
    // and use the explicit-confirmed server fallback (never return original HEIC
    // as native_ready; never create a native↔server loop).
    const editorFile = await materializeBitmapToJpegFile(decoded, options);
    if (!stillActive()) {
      cleanupOwned();
      return cancelledResult();
    }

    if (!editorFile) {
      cleanupOwned();
      return routeToServer();
    }

    // Close bitmap after successful materialize; editor re-decodes the JPEG File.
    cleanupOwned();

    if (!stillActive()) {
      return cancelledResult();
    }

    emit(PREPARE_STATES.native_ready);
    return {
      kind: "native",
      state: PREPARE_STATES.native_ready,
      runId,
      file: editorFile,
    };
  } catch (_nativeErr) {
    // Native failure / unavailable → server preview after OD-4 confirmation.
    cleanupOwned();
    if (!stillActive()) {
      return cancelledResult();
    }
    return routeToServer();
  }
}

/**
 * Small helper for UI owners: create AbortController + runId pair.
 * Callers must abort AND invalidate the active run id on unmount/replace.
 */
export function createPrepareSession() {
  const runId = nextPrepareRunId();
  const controller = new AbortController();
  return {
    runId,
    controller,
    signal: controller.signal,
    abort() {
      controller.abort();
    },
    isCurrent(activeRunId) {
      return activeRunId === runId && !controller.signal.aborted;
    },
  };
}
